import { useState, useEffect, useMemo } from 'react';
import { Search, Download, Trash2, ChevronLeft, ChevronRight, Loader2, Gift } from 'lucide-react';
import { adminApi, TuVan as TuVanType } from '../services/adminApi';

const statusLabels: Record<string, string> = {
  moi: 'Mới',
  dang_xu_ly: 'Đang xử lý',
  da_xu_ly: 'Đã xử lý',
};

function normalizeVN(s: string): string {
  return (s || '')
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .replace(/đ/g, 'd')
    .replace(/Đ/g, 'D')
    .toLowerCase()
    .trim();
}

const ITEMS_PER_PAGE = 10;

export default function TuVan() {
  const [items, setItems] = useState<TuVanType[]>([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [statusFilter, setStatusFilter] = useState('all');
  const [page, setPage] = useState(1);
  const [updatingId, setUpdatingId] = useState<string | null>(null);

  const fetchData = async () => {
    try {
      setLoading(true);
      const res = await adminApi.getTuVan();
      if (res.success && res.data) setItems(res.data);
    } catch (err) {
      console.error(err);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    fetchData();
  }, []);

  const filtered = useMemo(() => {
    const q = normalizeVN(search);
    return items.filter((c) => {
      const matchSearch =
        q === '' ||
        normalizeVN(c.fullName).includes(q) ||
        normalizeVN(c.phoneNumber).includes(q) ||
        normalizeVN(c.shopLink).includes(q) ||
        normalizeVN(c.shopIssue).includes(q);
      const matchStatus = statusFilter === 'all' || c.trangThai === statusFilter;
      return matchSearch && matchStatus;
    });
  }, [items, search, statusFilter]);

  useEffect(() => {
    setPage(1);
  }, [search, statusFilter]);

  const totalPages = Math.ceil(filtered.length / ITEMS_PER_PAGE);
  const startIndex = (page - 1) * ITEMS_PER_PAGE;
  const current = filtered.slice(startIndex, startIndex + ITEMS_PER_PAGE);

  const changeStatus = async (id: string, trangThai: TuVanType['trangThai']) => {
    setUpdatingId(id);
    try {
      const res = await adminApi.updateTuVanStatus(id, trangThai);
      if (res.success) {
        setItems((prev) => prev.map((c) => (c.id === id ? { ...c, trangThai } : c)));
      }
    } catch (err) {
      console.error(err);
    } finally {
      setUpdatingId(null);
    }
  };

  const remove = async (id: string) => {
    if (!window.confirm('Bạn có chắc muốn xoá bản ghi này?')) return;
    try {
      const res = await adminApi.deleteTuVan(id);
      if (res.success) setItems((prev) => prev.filter((c) => c.id !== id));
    } catch (err) {
      console.error(err);
    }
  };

  const formatDate = (s: string) =>
    new Date(s).toLocaleString('vi-VN', {
      day: '2-digit',
      month: '2-digit',
      year: 'numeric',
      hour: '2-digit',
      minute: '2-digit',
    });

  const exportCSV = () => {
    const headers = ['STT', 'Họ tên', 'SĐT', 'Vấn đề', 'Doanh thu', 'Link Shop', 'Phần thưởng', 'Nguồn', 'Trạng thái', 'Thời gian'];
    const rows = filtered.map((c, i) => [
      i + 1,
      c.fullName,
      c.phoneNumber,
      c.shopIssue || '',
      c.revenueScale || '',
      c.shopLink || '',
      c.prizeWon || '',
      c.source || '',
      statusLabels[c.trangThai] || '',
      formatDate(c.createdAt),
    ]);
    const csv = [headers.join(','), ...rows.map((r) => r.map((cell) => `"${cell}"`).join(','))].join('\n');
    const blob = new Blob(['﻿' + csv], { type: 'text/csv;charset=utf-8;' });
    const url = URL.createObjectURL(blob);
    const link = document.createElement('a');
    link.href = url;
    link.download = `tu-van-${new Date().toISOString().split('T')[0]}.csv`;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
    URL.revokeObjectURL(url);
  };

  return (
    <div className="admin-container wide">
      <div className="page-head">
        <div>
          <h2 className="page-title">Quản Lý Tư Vấn</h2>
          <p className="page-subtitle">Danh sách khách để lại thông tin tư vấn</p>
        </div>
      </div>

      <div className="card" style={{ overflow: 'hidden' }}>
        <div className="toolbar">
          <div className="search-wrap">
            <Search size={18} className="search-icon" />
            <input
              className="input"
              placeholder="Tìm theo tên, SĐT, link shop, vấn đề..."
              value={search}
              onChange={(e) => setSearch(e.target.value)}
            />
          </div>
          <select className="select" value={statusFilter} onChange={(e) => setStatusFilter(e.target.value)}>
            <option value="all">Tất cả trạng thái</option>
            <option value="moi">Mới</option>
            <option value="dang_xu_ly">Đang xử lý</option>
            <option value="da_xu_ly">Đã xử lý</option>
          </select>
          <button className="btn btn-success" onClick={exportCSV} disabled={filtered.length === 0}>
            <Download size={18} /> Xuất CSV
          </button>
        </div>

        {loading ? (
          <div className="center-screen">
            <Loader2 size={32} className="spin" style={{ color: 'var(--red-600)' }} />
          </div>
        ) : filtered.length === 0 ? (
          <div className="empty-state">Không có dữ liệu</div>
        ) : (
          <>
            <div style={{ overflowX: 'auto' }}>
              <table className="admin-table">
                <thead>
                  <tr>
                    <th>STT</th>
                    <th>Họ tên</th>
                    <th>SĐT</th>
                    <th>Vấn đề</th>
                    <th>Phần thưởng</th>
                    <th>Link Shop</th>
                    <th>Thời gian</th>
                    <th>Trạng thái</th>
                    <th className="t-center">Xoá</th>
                  </tr>
                </thead>
                <tbody>
                  {current.map((c, i) => (
                    <tr key={c.id}>
                      <td className="muted">{startIndex + i + 1}</td>
                      <td style={{ fontWeight: 600, color: 'var(--slate-800)' }}>{c.fullName}</td>
                      <td>{c.phoneNumber}</td>
                      <td>{c.shopIssue || '—'}</td>
                      <td>
                        {c.prizeWon ? (
                          <span className="badge badge-amber">
                            <Gift size={12} /> {c.prizeWon}
                          </span>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td>
                        {c.shopLink ? (
                          <a className="link truncate" href={c.shopLink} target="_blank" rel="noopener noreferrer">
                            {c.shopLink}
                          </a>
                        ) : (
                          <span className="muted">—</span>
                        )}
                      </td>
                      <td className="muted" style={{ whiteSpace: 'nowrap' }}>{formatDate(c.createdAt)}</td>
                      <td>
                        {updatingId === c.id ? (
                          <Loader2 size={16} className="spin" />
                        ) : (
                          <select
                            className="select"
                            style={{ padding: '5px 8px', fontSize: '0.8rem' }}
                            value={c.trangThai}
                            onChange={(e) => changeStatus(c.id, e.target.value as TuVanType['trangThai'])}
                          >
                            <option value="moi">Mới</option>
                            <option value="dang_xu_ly">Đang xử lý</option>
                            <option value="da_xu_ly">Đã xử lý</option>
                          </select>
                        )}
                      </td>
                      <td className="t-center">
                        <button className="btn-icon danger" onClick={() => remove(c.id)} title="Xoá">
                          <Trash2 size={16} />
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>

            {totalPages > 1 && (
              <div className="pagination">
                <div className="muted" style={{ fontSize: '0.85rem' }}>
                  {startIndex + 1}–{Math.min(startIndex + ITEMS_PER_PAGE, filtered.length)} / {filtered.length} bản ghi
                </div>
                <div className="row">
                  <button className="page-btn" onClick={() => setPage((p) => Math.max(1, p - 1))} disabled={page === 1}>
                    <ChevronLeft size={18} />
                  </button>
                  {Array.from({ length: totalPages }, (_, i) => i + 1).map((p) => (
                    <button key={p} className={`page-btn${page === p ? ' active' : ''}`} onClick={() => setPage(p)}>
                      {p}
                    </button>
                  ))}
                  <button
                    className="page-btn"
                    onClick={() => setPage((p) => Math.min(totalPages, p + 1))}
                    disabled={page === totalPages}
                  >
                    <ChevronRight size={18} />
                  </button>
                </div>
              </div>
            )}
          </>
        )}
      </div>
    </div>
  );
}
